import React, { useMemo, useCallback, useRef, useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { css } from "@emotion/react";
import { Node, Editor, Transforms, Range, createEditor } from "slate";
import { Slate, Editable, ReactEditor, withReact } from "slate-react";
import { IS_NODE_MAP_DIRTY } from "slate-dom";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { colors } from "style";
import { withMentions, MentionTag, MentionList, insertMention } from "./Plugins/Mentions";
import withHtml from "./Plugins/withHtml";
import withMarkdown from "./Plugins/withMarkdown";
import FormattingBar from "./FormattingBar";

/**
 * TextEditor
 *
 * @param {Array}    value
 * @param {String}   name
 * @param {Function} onChange
 * @param {Function} onFocus
 * @param {Function} readOnly
 * @param {Function} placeholder
 * @param {Function} showFormattingOptions
 * @param {Object}   editableProps Accessibility and input props forwarded to the editable area
 */
const TextEditor = forwardRef(
  (
    { value, name, onChange, onFocus, readOnly, placeholder, showFormattingOptions = false, editableProps, ...props },
    ref
  ) => {
    const container = useRef(null);
    const [target, setTarget] = useState();
    const [isFocused, setFocus] = useState(false);
    const [index, setIndex] = useState(0);
    const [search, setSearch] = useState("");
    const [editableKey, setEditableKey] = useState(0);
    const renderElement = useCallback((props) => <Element {...props} />, []);
    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
    const editor = useMemo(() => withMarkdown(withHtml(withMentions(withReact(createEditor())))), []);

    const safeValue = useMemo(() => {
      if (Array.isArray(value)) {
        // clone to avoid shared references between editors
        return JSON.parse(JSON.stringify(value));
      }
      return createDefaultValue();
    }, [value]);

    const handleChange = useCallback(
      (value) => {
        const { selection } = editor;

        if (typeof onChange === "function") {
          onChange(name, value, editor);
        }

        if (selection && Range.isCollapsed(selection)) {
          const [start] = Range.edges(selection);
          const wordBefore = Editor.before(editor, start, { unit: "word" });
          const before = wordBefore && Editor.before(editor, wordBefore);
          const beforeRange = before && Editor.range(editor, before, start);
          const beforeText = beforeRange && Editor.string(editor, beforeRange);
          const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/);
          const after = Editor.after(editor, start);
          const afterRange = Editor.range(editor, start, after);
          const afterText = Editor.string(editor, afterRange);
          const afterMatch = afterText.match(/^(\s|$)/);

          if (beforeMatch && afterMatch) {
            setTarget(beforeRange);
            setSearch(beforeMatch[1]);
            setIndex(0);
            return;
          }
        }

        setTarget(null);
      },
      [editor]
    );

    const handleFocus = useCallback(
      (e) => {
        if (e.target === container.current) {
          ReactEditor.focus(editor);
          e.preventDefault();
        }
      },
      [editor]
    );

    // An extension or the OS edited the DOM behind Slate: drop this input and rebuild the DOM from the document
    const handleDOMBeforeInput = useCallback(
      (event) => {
        const { inputType } = event;

        if ((inputType.startsWith("delete") && !inputType.startsWith("deleteBy")) || IS_NODE_MAP_DIRTY.get(editor)) {
          return;
        }

        const [targetRange] = event.getTargetRanges();

        if (!targetRange || ReactEditor.toSlateRange(editor, targetRange, { exactMatch: false, suppressThrow: true })) {
          return;
        }

        event.preventDefault();
        setEditableKey((key) => key + 1);
        return true;
      },
      [editor]
    );

    useEffect(() => {
      if (editableKey > 0) {
        ReactEditor.blur(editor);
        ReactEditor.focus(editor);
      }
    }, [editableKey]);

    useEffect(() => {
      onFocus?.(isFocused);
    }, [isFocused]);

    // Expose some methods to parent
    useImperativeHandle(
      ref,
      () => {
        return {
          focus(moveToEnd) {
            ReactEditor.focus(editor);

            if (moveToEnd) {
              Transforms.select(editor, Editor.end(editor, []));
            }
          },
          addMention(item) {
            ReactEditor.focus(editor);
            Transforms.select(editor, Editor.end(editor, []));
            insertMention(editor, item);
          },
          reset() {
            const point = { path: [0, 0], offset: 0 };

            editor.selection = { anchor: point, focus: point };
            editor.history = { redos: [], undos: [] };
            editor.children = createDefaultValue();
            editor.onChange();
          },
        };
      },
      [editor]
    );

    return (
      <Container ref={container} isFocused={isFocused} readOnly={readOnly} onMouseDown={handleFocus} {...props}>
        {!readOnly && showFormattingOptions && <FormattingBar editor={editor} />}
        <Slate editor={editor} initialValue={safeValue} onChange={handleChange}>
          <Editable
            key={editableKey}
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            {...editableProps}
            onDOMBeforeInput={handleDOMBeforeInput}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            readOnly={readOnly}
            css={!readOnly ? styles.editor : ""}
          />
          {!readOnly && (
            <MentionList
              index={index}
              editor={editor}
              target={target}
              search={search}
              onSelect={(i, item) => {
                Transforms.select(editor, target);
                setIndex(i);
                insertMention(editor, item);
                setTarget(null);
                ReactEditor.focus(editor);
              }}
              containerRef={container}
            />
          )}
        </Slate>
      </Container>
    );
  }
);

const getPlainText = (value) => value?.map((n) => Node.string(n)).join("\n");

const convertFromPlainText = (value) => {
  return value?.split("\n")?.map((line) => ({
    type: "paragraph",
    children: [{ text: line }],
  }));
};

const Container = styled.div`
  ${(props) =>
    !props.readOnly &&
    `
    min-height: 15rem;
    display: flex;
    flex-direction: column;
  `}

  order: 1;
  border-radius: 0.6rem;
  background-color: none;
  padding: 1rem 1rem;
  line-height: ${({ lineHeight }) => lineHeight || "1.5"};
  border: 1px solid ${colors.grayAnatomyLight3};
  outline: none;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: ${colors.purpleRainBase};
  }

  p {
    margin-top: 0;
  }

  ol,
  ul {
    margin: 1rem 1rem;
  }

  ${(props) =>
    props.isFocused &&
    `
    border-color: ${colors.purpleRainBase};
    box-shadow: 0px 0px 0px 3px ${colors.purpleRainLight3};

    & + label {
      color: ${colors.purpleRainBase};
    }
  `}
`;

/**
 * Leaf
 *
 * @param {Object}  attributes
 * @param {Object}  children
 * @param {Object}  leaf
 */
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

/**
 * Element
 */
const Element = (props) => {
  const { attributes, children, element } = props;

  switch (element.type) {
    case "mention":
      return <MentionTag {...props} />;

    case "bulleted-list":
      return <ul {...attributes}>{children}</ul>;

    case "list-item":
      return <li {...attributes}>{children}</li>;

    case "numbered-list":
      return <ol {...attributes}>{children}</ol>;

    default:
      return <p {...attributes}>{children}</p>;
  }
};

const styles = {
  editor: css`
    &:focus {
      outline: none;
      box-shadow: none;
    }
  `,
};

const createDefaultValue = () => [
  {
    type: "paragraph",
    children: [{ text: "" }],
  },
];

TextEditor.displayName = "TextEditor";

TextEditor.propTypes = {
  value: PropTypes.array,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  showFormattingOptions: PropTypes.bool,
  editableProps: PropTypes.object,
};

Leaf.propTypes = {
  attributes: PropTypes.object,
  children: PropTypes.object,
  leaf: PropTypes.object,
};

Element.propTypes = {
  attributes: PropTypes.object,
  children: PropTypes.object,
  element: PropTypes.object,
};

export { createDefaultValue, getPlainText, convertFromPlainText };

export default TextEditor;
